import { Fetch, GetParams } from "./fetch.types";
import { createGetURL } from '../../utils';

export const fetchSymbol = Symbol('fetch');

export class FetchHelperError {
  constructor(
    readonly status: number,
    readonly message: string,
    readonly description: string,
    readonly response?: Response,
    readonly error?: Error
  ) {}
}


export class FetchHelper {
  constructor(
    readonly fetch: Fetch, 
    readonly pureFetch?: Fetch
  ) {}

  static getLocaleURL(url: string): string {
    const localRoot = window.Shopify.routes.root;
    if (url.startsWith(localRoot)) return url;
    if (url.startsWith('/')) return localRoot + url.slice(1);
    if (url.startsWith(window.location.origin)) return localRoot + url.slice(window.location.origin.length + 1);
    return url;
  }

  private execFetch(pureFetch: boolean = false, url: string, req?: RequestInit): Promise<Response> {
    const fetch = pureFetch && this.pureFetch || this.fetch;
    const localURL = FetchHelper.getLocaleURL(url);
    return fetch(localURL, req);
  }

  /**
   * GET a url with paramater (transformed into URL parameters)
   * @param url The URL to request
   * @param params The paramaters object
   * @returns Fetch resoponse
   */
  get(url: string, params?: GetParams, pureFetch?: boolean): Promise<Response> {
    return this.execFetch(pureFetch, createGetURL(url, params));
  }

  /**
   * POST to a url 
   * @param url The URL to request
   * @param body The body of the POST request, either an Object, a stringified Object, a FormData, or a DOM form element
   * @param headerContentType The content-type header
   * @returns Fetch response
   */
  post(url: string, body: object | FormData | string | HTMLFormElement, headerContentType?: string, pureFetch?: boolean): Promise<Response> {
    let fetchBody: string | FormData = '';
    let contentType: string | undefined = headerContentType;

    if (body instanceof HTMLFormElement) {
      fetchBody = new FormData(body);
    } else if (body instanceof FormData) {
      fetchBody = body;
    } else if (typeof body === 'string') {
      fetchBody = body;
    } else {
      fetchBody = JSON.stringify(body);
      contentType = 'application/json';
    }
    const reqInit: RequestInit = {
      body: fetchBody,
      method: 'POST'
    };

    if (contentType) reqInit.headers = { 'Content-Type': contentType };

    return this.execFetch(pureFetch, url, reqInit);
  }


  /**
   * Send a `GET` request to get a JSON result 
   * @param url The URL endpoint to request
   * @param params The GET paramaters as an object
   * @returns The requested object
   */
  async getJSON<t extends object | null>(url: string, params?: GetParams, pureFetch?: boolean): Promise<t> {
    const res = await this.get(url, params, pureFetch);
    if (res.ok) {
      return res.json();
    } else {
      throw this.handleError(res);
    }
  }

  /**
   * Send a `GET` request to get a Text result 
   * @param url The URL endpoint to request
   * @param params The GET paramaters as an object
   * @returns The requested text content
   */
  async getText(url: string, params?: GetParams, pureFetch?: boolean): Promise<string> {
    const res = await this.get(url, params, pureFetch);
    if (res.ok) {
      return res.text();
    } else {
      throw this.handleError(res);
    }
  }

  /**
   * Send a `GET` request to get an HTML Document as a result 
   * @param url The URL endpoint to request
   * @param params The GET paramaters as an object
   * @returns A document element with the HTML content
   */
  async getHTML(url: string, params?: GetParams, pureFetch?: boolean): Promise<Document> {
    const res = await this.getText(url, params, pureFetch);
    return (new DOMParser()).parseFromString(res, 'text/html');
  }

  /**
   * Send a `POST` request with a JSON body
   * @param url The URL endpoint to POST to
   * @param body The object body to POST
   * @returns The object reponse, or a null
   */
  async postJSON<Response extends object | null, Params extends object = any>(url: string, body: Params, pureFetch?: boolean): Promise<Response> {
    const res = await this.post(url, body, undefined, pureFetch);
    if (res.ok) {
      return res.json();
    } else {
      throw this.handleError(res);
    }
  }

  /**
   * Send a `POST` request with a form body
   * @param url The URL endpoint to POST to
   * @param body The object body to POST
   * @returns The fetch reponse
   */
  async postForm(url: string, body: FormData | string, pureFetch?: boolean): Promise<Response> {
    // const post = (body: FormData | HTMLFormElement) => this.post(url, body);
    let postBody: FormData | HTMLFormElement;
    if (typeof body === 'string') {
      const bodyForm = document.querySelector(body);
      if (bodyForm && bodyForm instanceof HTMLFormElement) {
        postBody = bodyForm;
      } else {
        // TODO: write a proper error message
        throw new Error(`didn't find a form`);
      }
    } else {
      postBody = body;
    }

    const res = await this.post(url, postBody, undefined, pureFetch);
    if (res.ok) {
      return res;
    } else {
      throw this.handleError(res);
    }
  }

  /** @ignore */
  private async handleError(res: Response): Promise<FetchHelperError> {
    const responseClone = res.clone();
    const content = await res.text();
    let status: number, message: string, description: string;
    try {
      const jsonContent = JSON.parse(content);
      status = jsonContent.status || res.status;
      message = jsonContent.message || res.statusText;
      description = jsonContent.description || '';
    } catch (err) {
      status = res.status;
      message = res.statusText;
      description = content || '';
    }
    return new FetchHelperError(status, message, description, responseClone);
  }
}